// @flow
// Import modules
import React, { Component } from 'react';
import ClassNames from 'classnames';
import find from 'lodash/find';
import { Row, Col } from '@riseart/antd-provider';
import { LikeIcon, DislikeIcon } from '@riseart/icons';
import { Button, Heading, MediaQuery, Swipeable } from '@riseart/common';
import { extractImageFileFromData } from 'shared_services/riseart/utils/ImageUtils';
import { Picture } from 'shared_components/common/artdirection/picture/Picture';

import {
  raScreenXsMax,
  raScreenSm,
  raScreenMdMax,
  raScreenLg,
} from '@riseart/antd-provider/dist/website/variables.less';
import { quizQuestionActiveCls } from 'shared_components/quiz/common/questions/Question.less';
import {
  quizImageSliderQuestionCls,
  quizImageSliderQuestionTextCls,
  quizImageSliderQuestionImageCls,
  quizImageSliderQuestionButtonsCls,
  quizImageSliderQuestionButtonsContainerCls,
  quizImageSliderQuestionOverlayCls,
  quizImageSliderQuestionOverlayLikeCls,
  quizImageSliderQuestionOverlayDislikeCls,
  quizImageSliderQuestionOverlayDislikeIconCls,
  quizImageSliderQuestionOverlayLikeIconCls,
  // Precalculated correction vars for item and image container
  raContainerHeightCorrectionSm,
  raContainerHeightCorrectionMd,
  raContainerHeightCorrectionLg,
  raImageHeightCorrectionSm,
  raImageHeightCorrectionMd,
  raImageHeightCorrectionLg,
} from './ImageSlider.less';

const SWIPE_DIRECTION = {
  LEFT: 'left',
  RIGHT: 'right',
};

const DIRECTION_TO_OPTION = {
  [SWIPE_DIRECTION.LEFT]: 'dislike',
  [SWIPE_DIRECTION.RIGHT]: 'like',
};

const CLASSNAME_TRANSITIONS = {
  like: quizImageSliderQuestionOverlayLikeCls,
  dislike: quizImageSliderQuestionOverlayDislikeCls,
};

type DirectionType = 'left' | 'right';

type Props = {
  id: number,
  type: string,
  text: string,
  order: number,
  images: Array<Object>,
  options: Array<*>,
  backButton: {
    translationText: string | Object,
    backListener: Function,
  },
  nextButton: {
    translationText: string | Object,
    nextListener: Function,
  },
  onAnswer: Function,
  onChange: Function,
  onSelectQuestion: Function,
  isActive: boolean,
  error: Object,
  totalMessagesHeight: number | string,
};

type State = {
  swipeAction?: ?string,
};

/**
 * Quiz Image Slider Question
 *
 * @param {Props} props
 * @returns {XML}
 */
class QuizImageSliderQuestion extends Component<Props, State> {
  /**
   * constructor
   *
   * @param {*} props
   */
  constructor(props: void) {
    super(props);

    this.bindMethods();

    this.state = { swipeAction: null };
  }

  /**
   * bindMethods
   */
  bindMethods() {
    this.handleClick = this.handleClick.bind(this);
    this.answerQuestion = this.answerQuestion.bind(this);
    this.handleSwipe = this.handleSwipe.bind(this);
  }

  /**
   * handleClick
   * @param {Object} option
   */
  handleClick =
    (option: Object): Function =>
    (e: Event): void => {
      e.preventDefault();
      this.answerQuestion(option);
    };

  /**
   * answerQuestion
   * @param {Object} option
   * @returns {void}
   */
  answerQuestion = (option: Object = {}): void => {
    const { id, type, onChange, onSelectQuestion } = this.props;

    onChange({ id, type }, option.id);
    // HACK: timeout the onSelectQuestion so that no rerendering
    // is happening when the slide animation has started
    setTimeout(() => onSelectQuestion(id), 100);
  };

  /**
   * handleSwipe
   *
   * @param {DirectionType} direction
   * @returns {Function}
   */
  handleSwipe =
    (direction: DirectionType = SWIPE_DIRECTION.LEFT): Function =>
    (): void => {
      const foundOption = find(this.props.options, ['type', DIRECTION_TO_OPTION[direction]]);

      if (!foundOption) {
        return;
      }

      this.setState({ swipeAction: DIRECTION_TO_OPTION[direction] });

      this.answerQuestion(foundOption);
    };

  /**
   * getButtons
   * @returns {react.Element<*>}
   */
  // TODO: @Diyana[white-rhino] Refactor to check by type for a specific option
  getButtons = () => {
    const tabIndex = this.props.isActive ? '0' : '-1';
    return (
      this.props.options.length === 2 && (
        <Row className={quizImageSliderQuestionButtonsCls}>
          <Col span={12} key={`img-option-${this.props.options[1].id}`}>
            <Button
              tabIndex={tabIndex}
              type="link"
              linkType="strong"
              icon={<DislikeIcon />}
              size="large"
              onClick={this.handleClick(this.props.options[1])}
            />
          </Col>
          <Col span={12} key={`img-option-${this.props.options[0].id}`}>
            <Button
              tabIndex={tabIndex}
              type="link"
              linkType="strong"
              icon={<LikeIcon />}
              size="large"
              onClick={this.handleClick(this.props.options[0])}
            />
          </Col>
        </Row>
      )
    );
  };

  /**
   * getText
   * @returns {react.Element<*>}
   */
  getText = () => (
    <Heading tag="h2" align="center" className={quizImageSliderQuestionTextCls}>
      {this.props.text}
    </Heading>
  );

  render() {
    const { images, isActive, totalMessagesHeight } = this.props;

    return (
      <MediaQuery minWidth={0} maxWidth={raScreenXsMax}>
        {(isScreenSm) => (
          <MediaQuery minWidth={raScreenSm} maxWidth={raScreenMdMax}>
            {(isScreenMd) => (
              <MediaQuery minWidth={raScreenLg}>
                {(isScreenLg) => {
                  let questionWrapperStyle = null;

                  if (isScreenSm) {
                    questionWrapperStyle = {
                      height: `calc(100vh - ${totalMessagesHeight}px - ${raContainerHeightCorrectionSm})`,
                    };
                  } else if (isScreenMd) {
                    questionWrapperStyle = {
                      height: `calc(100vh - ${totalMessagesHeight}px - ${raContainerHeightCorrectionMd})`,
                    };
                  } else if (isScreenLg) {
                    questionWrapperStyle = {
                      height: `calc(100vh - ${totalMessagesHeight}px - ${raContainerHeightCorrectionLg})`,
                    };
                  }

                  return (
                    <div
                      className={ClassNames(quizImageSliderQuestionCls, {
                        [quizQuestionActiveCls]: isActive,
                      })}
                      style={questionWrapperStyle}
                    >
                      <div
                        className={ClassNames(
                          quizImageSliderQuestionOverlayCls,
                          this.state.swipeAction && CLASSNAME_TRANSITIONS[this.state.swipeAction],
                        )}
                      >
                        <LikeIcon className={quizImageSliderQuestionOverlayLikeIconCls} />
                        <DislikeIcon className={quizImageSliderQuestionOverlayDislikeIconCls} />
                      </div>

                      <Swipeable
                        onSwipedLeft={this.handleSwipe(SWIPE_DIRECTION.LEFT)}
                        onSwipedRight={this.handleSwipe(SWIPE_DIRECTION.RIGHT)}
                      >
                        <Row>
                          {isScreenSm ? (
                            <React.Fragment>
                              <Col span={24}>{this.getText()}</Col>
                              <Col span={24}>
                                <div
                                  className={quizImageSliderQuestionImageCls}
                                  style={{
                                    height: `calc(100vh - ${totalMessagesHeight}px - ${raImageHeightCorrectionSm})`,
                                  }}
                                >
                                  <Picture
                                    type="quiz.questions"
                                    artDirectionKey="quizQuestion.main"
                                    image={extractImageFileFromData.byType(images, 'small')}
                                    alt=""
                                  />
                                </div>
                              </Col>
                              <Col span={24} className={quizImageSliderQuestionButtonsContainerCls}>
                                {this.getButtons()}
                              </Col>
                            </React.Fragment>
                          ) : null}
                          {isScreenMd ? (
                            <React.Fragment>
                              <Col span={24}>{this.getText()}</Col>
                              <Col span={24}>
                                <div
                                  className={quizImageSliderQuestionImageCls}
                                  style={{
                                    height: `calc(100vh - ${totalMessagesHeight}px - ${raImageHeightCorrectionMd})`,
                                  }}
                                >
                                  <Picture
                                    type="quiz.questions"
                                    artDirectionKey="quizQuestion.main"
                                    image={extractImageFileFromData.byType(images, 'medium')}
                                    alt=""
                                  />
                                </div>
                              </Col>
                              <Col span={24} className={quizImageSliderQuestionButtonsContainerCls}>
                                {this.getButtons()}
                              </Col>
                            </React.Fragment>
                          ) : null}
                          {isScreenLg ? (
                            <React.Fragment>
                              <Col span={13}>
                                <div
                                  className={quizImageSliderQuestionImageCls}
                                  style={{
                                    height: `calc(100vh - ${totalMessagesHeight}px - ${raImageHeightCorrectionLg})`,
                                  }}
                                >
                                  <Picture
                                    type="quiz.questions"
                                    artDirectionKey="quizQuestion.main"
                                    image={extractImageFileFromData.byType(images, 'large')}
                                    alt=""
                                  />
                                </div>
                              </Col>
                              <Col span={2} />
                              <Col span={7}>
                                {this.getText()}
                                {this.getButtons()}
                              </Col>
                            </React.Fragment>
                          ) : null}
                        </Row>
                      </Swipeable>
                    </div>
                  );
                }}
              </MediaQuery>
            )}
          </MediaQuery>
        )}
      </MediaQuery>
    );
  }
}

export default QuizImageSliderQuestion;
